import type { EChartsOption } from 'echarts';

import { graphConfig } from '@/configs/Graphs';
import { BehaviorChart } from '@/features/forecasts/components/order-behaviour/BehaviorChart';
import { useOrderData } from '@/features/forecasts/hooks/useOrderData';
import { buildTooltipSeriesLine, joinTooltipLines } from '@/features/forecasts/utils/forecastTooltip';
import { findNearbyUsFederalHoliday, formatIsoDateMd } from '@/utils/date';
import { format } from '@/utils/format';
import { createNoDataOption } from '@/utils/graphs';
import { mostFrequent } from '@/utils/sort';

// Heroicons CalendarIcon (20/solid) inlined as a data URI for ECharts image:// usage
const CALENDAR_ICON =
  "image://data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z' clip-rule='evenodd'/%3E%3C/svg%3E";
const CALENDAR_ICON_URL =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.75 2a.75.75 0 0 1 .75.75V4h7V2.75a.75.75 0 0 1 1.5 0V4h.25A2.75 2.75 0 0 1 18 6.75v8.5A2.75 2.75 0 0 1 15.25 18H4.75A2.75 2.75 0 0 1 2 15.25v-8.5A2.75 2.75 0 0 1 4.75 4H5V2.75A.75.75 0 0 1 5.75 2Zm-1 5.5c-.69 0-1.25.56-1.25 1.25v6.5c0 .69.56 1.25 1.25 1.25h10.5c.69 0 1.25-.56 1.25-1.25v-6.5c0-.69-.56-1.25-1.25-1.25H4.75Z' clip-rule='evenodd'/%3E%3C/svg%3E";
const MAX_DISPLAY_INTERARRIVAL_DAYS = 49;
const MIN_NON_ZERO_DISPLAY_VALUE = 0.5;

function mapZeroToDisplayValue(value: number) {
  return value === 0 ? MIN_NON_ZERO_DISPLAY_VALUE : value;
}

type Props = {
  chartGroup?: string;
};

export function OrderBehaviorInterarrival({ chartGroup }: Props) {
  const { records, total } = useOrderData();

  const { axisLabel, grid, colors } = graphConfig;
  const orderCount = format('Last {{total}} orders', { total });
  const interarrivalValues = records
    .map((record) => record.interarrival_days)
    .filter((value): value is number => typeof value === 'number' && Number.isFinite(value) && value >= 0);
  const mostFrequentDays = mostFrequent(interarrivalValues) ?? 'N/A';
  const subtitle = format('Most frequently requested: {{mostFrequentDays}} days • {{orderCount}}', {
    mostFrequentDays,
    orderCount,
  });

  const maxInDataSet = records.reduce((max, r) => Math.max(max, mapZeroToDisplayValue(r.interarrival_days ?? 0)), 0);
  const maxYaxis = Math.min(MAX_DISPLAY_INTERARRIVAL_DAYS, maxInDataSet);

  const holidayMarkers = records
    .map((d) => ({ record: d, holidayName: findNearbyUsFederalHoliday(d.date) }))
    .filter((d) => !!d.holidayName)
    .map((d) => ({
      name: d.holidayName,
      coord: [`${d.record.order_sequence ?? ''}`, mapZeroToDisplayValue(d.record.interarrival_days ?? 0) + 2],
    }));

  const chartOptions = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      /* c8 ignore start */
      formatter: (params) => {
        const p = Array.isArray(params) ? params[0] : params;
        const data = p.data as { value: number; timestamp?: string };
        const dateLabel = formatIsoDateMd(data.timestamp ?? '');
        const holidayName = findNearbyUsFederalHoliday(data.timestamp);
        return joinTooltipLines([
          dateLabel && buildTooltipSeriesLine({ label: 'Date', value: dateLabel }),
          data.value != null && buildTooltipSeriesLine({ label: 'Days since previous order', value: data.value }),
          holidayName && buildTooltipSeriesLine({ label: 'Holiday', value: holidayName }),
        ]);
      },
      /* c8 ignore stop */
    },
    graphic: holidayMarkers.length
      ? [
          {
            type: 'group',
            right: 8,
            top: 0,
            children: [
              { type: 'image', style: { image: CALENDAR_ICON_URL, width: 14, height: 14 }, top: -1 },
              {
                type: 'text',
                left: 18,
                top: 1,
                style: { text: 'Holiday', fontSize: 11, fill: '#6b7280' },
              },
            ],
          },
        ]
      : undefined,
    grid,
    xAxis: {
      type: 'category',
      // Plotted against order sequence (0,1,2,…) so spacing is even regardless of
      // gaps between order dates; the date stays available in the tooltip.
      data: records.map((d) => `${d.order_sequence ?? ''}`),
      axisLabel,
    },
    yAxis: { type: 'value', axisLabel, max: maxYaxis, interval: 7 },
    dataZoom: [{ type: 'slider', filterMode: 'none', bottom: 5, height: 20 }],
    series: [
      {
        type: 'bar',
        data: records.map((d) => {
          const rawValue = d.interarrival_days ?? 0;
          const value = mapZeroToDisplayValue(rawValue);
          return {
            value,
            timestamp: d.date,
            itemStyle: { color: rawValue >= MAX_DISPLAY_INTERARRIVAL_DAYS ? colors.orange : colors.black },
          };
        }),
        markPoint: {
          symbol: CALENDAR_ICON,
          symbolSize: 18,
          symbolOffset: [0, -6],
          silent: true,
          label: { show: false },
          data: holidayMarkers,
        },
      },
    ],
  } satisfies EChartsOption;

  return (
    <BehaviorChart
      chartGroup={chartGroup}
      option={total ? chartOptions : createNoDataOption('No data available')}
      title={'Time between Order Request Dates'}
      titleInfo={subtitle}
    />
  );
}
